import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UserPlus, FolderSimple, UserMinus } from "@phosphor-icons/react";
import ClientCard from "@/components/app/ClientCard";
import ClientDialog from "@/components/app/ClientDialog";
import FolderDialog from "@/components/app/FolderDialog";
import MoveClientDialog from "@/components/app/MoveClientDialog";
import KpiFilterBanner from "@/components/app/clients/KpiFilterBanner";
import ClientsSearchFilter from "@/components/app/clients/ClientsSearchFilter";
import FoldersGrid from "@/components/app/clients/FoldersGrid";
import { FILTER_META } from "@/components/app/clients/filterMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_CLIENT = { first_name: "", last_name: "", email: "", phone: "", folder_id: "" };
const EMPTY_FOLDER = { name: "", description: "" };

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [folders, setFolders] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [folderForm, setFolderForm] = useState(EMPTY_FOLDER);
  const [movingClient, setMovingClient] = useState(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState("none");
  const [filterIds, setFilterIds] = useState(null); // null = no filter, [] = filter empty, [...] = filter set
  const [showInactive, setShowInactive] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = searchParams.get("filter");
  const filterMeta = filterKey ? FILTER_META[filterKey] : null;

  const fetchData = useCallback(async () => {
    try {
      const [cRes, fRes] = await Promise.all([
        axios.get(`${API}/clients`, { params: showInactive ? { include_inactive: true } : {} }),
        axios.get(`${API}/folders`),
      ]);
      setClients(cRes.data);
      setFolders(fRes.data);
    } catch (err) {
      console.error("[ClientsPage] fetchData failed", err);
      toast.error("Errore nel caricamento clienti");
    }
  }, [showInactive]);

  // Load dashboard-driven filter ids when filterKey is set
  useEffect(() => {
    if (!filterKey || !FILTER_META[filterKey]) {
      setFilterIds(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/stats`);
        const ids =
          filterKey === "inactive"
            ? data?.inactive_clients?.client_ids ?? []
            : filterKey === "improving"
            ? data?.improving_clients?.client_ids ?? []
            : [];
        if (!cancelled) setFilterIds(ids);
      } catch (err) {
        console.error("[ClientsPage] failed to load KPI filter ids", err);
        if (!cancelled) setFilterIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const matchSearch = `${c.first_name} ${c.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchFolder =
        selectedFolder === "all" ||
        c.folder_id === selectedFolder ||
        (selectedFolder === "none" && !c.folder_id);
      const matchKpiFilter = filterIds === null || filterIds.includes(c.id);
      return matchSearch && matchFolder && matchKpiFilter;
    });
  }, [clients, search, selectedFolder, filterIds]);

  const clearKpiFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("filter");
    setSearchParams(params);
  };

  const handleSaveClient = async () => {
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, clientForm);
        toast.success("Cliente aggiornato");
      } else {
        await axios.post(`${API}/clients`, clientForm);
        toast.success("Cliente creato");
      }
      setShowClientDialog(false);
      setEditingClient(null);
      setClientForm(EMPTY_CLIENT);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Cliente eliminato");
      fetchData();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSaveFolder = async () => {
    try {
      await axios.post(`${API}/folders`, folderForm);
      toast.success("Cartella creata");
      setShowFolderDialog(false);
      setFolderForm(EMPTY_FOLDER);
      fetchData();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm("Eliminare questa cartella? I clienti verranno spostati fuori.")) return;
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      toast.success("Cartella eliminata");
      if (selectedFolder === folderId) setSelectedFolder("all");
      fetchData();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      first_name: client.first_name,
      last_name: client.last_name,
      email: client.email || "",
      phone: client.phone || "",
      folder_id: client.folder_id || "",
    });
    setShowClientDialog(true);
  };

  const openMoveClient = (client) => {
    setMovingClient(client);
    setMoveTargetFolder(client.folder_id || "none");
  };

  const handleMoveClient = async () => {
    if (!movingClient) return;
    try {
      await axios.put(`${API}/clients/${movingClient.id}`, {
        folder_id: moveTargetFolder === "none" ? "" : moveTargetFolder,
      });
      const folderName = moveTargetFolder === "none"
        ? "Senza cartella"
        : folders.find((f) => f.id === moveTargetFolder)?.name || "";
      toast.success(`${movingClient.first_name} spostato in "${folderName}"`);
      setMovingClient(null);
      fetchData();
    } catch (err) {
      toast.error("Errore nello spostamento");
    }
  };

  const openNewClient = () => {
    setEditingClient(null);
    setClientForm(EMPTY_CLIENT);
    setShowClientDialog(true);
  };

  return (
    <div data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            CLIENTI
          </h1>
          <p className="text-zinc-500 text-sm mt-1">{clients.length} clienti totali</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowInactive((v) => !v)}
            variant="outline"
            className={`border-[#262626] gap-2 ${
              showInactive
                ? "bg-zinc-700/30 text-zinc-200 hover:bg-zinc-700/40"
                : "text-zinc-400 hover:bg-[#1A1A1A] hover:text-white"
            }`}
            data-testid="toggle-show-inactive"
            title="Mostra/Nascondi i clienti segnati come inattivi"
          >
            <UserMinus size={18} />
            <span className="hidden sm:inline">
              {showInactive ? "Nascondi inattivi" : "Mostra inattivi"}
            </span>
          </Button>
          <Button
            onClick={() => setShowFolderDialog(true)}
            variant="outline"
            className="border-[#262626] bg-transparent text-zinc-300 hover:bg-[#1A1A1A] hover:text-white"
            data-testid="create-folder-button"
          >
            <FolderSimple size={18} className="mr-2" /> Nuova Cartella
          </Button>
          <Button
            onClick={openNewClient}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="create-client-button"
          >
            <UserPlus size={18} className="mr-2" /> Nuovo Cliente
          </Button>
        </div>
      </div>

      <KpiFilterBanner
        filterMeta={filterMeta}
        filterIds={filterIds}
        filteredCount={filteredClients.length}
        onClear={clearKpiFilter}
      />

      <ClientsSearchFilter
        search={search}
        onSearch={setSearch}
        selectedFolder={selectedFolder}
        onSelectFolder={setSelectedFolder}
        folders={folders}
      />

      {selectedFolder === "all" && (
        <FoldersGrid
          folders={folders}
          clients={clients}
          onSelectFolder={setSelectedFolder}
          onDeleteFolder={handleDeleteFolder}
        />
      )}

      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client, i) => (
            <ClientCard
              key={client.id}
              client={client}
              index={i}
              onEdit={openEditClient}
              onMove={openMoveClient}
              onDelete={handleDeleteClient}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <UserPlus size={48} weight="duotone" className="text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg font-medium">
            {filterMeta
              ? `Nessun cliente ${filterMeta.label.toLowerCase()}`
              : "Nessun cliente trovato"}
          </p>
          <p className="text-zinc-600 text-sm mt-1">
            {filterMeta
              ? "Ottimo lavoro! Tutti i tuoi clienti sono attivi e in linea."
              : search
              ? "Prova una ricerca diversa"
              : "Inizia aggiungendo il tuo primo cliente"}
          </p>
          {filterMeta && (
            <button
              type="button"
              onClick={clearKpiFilter}
              className="mt-3 text-[#007AFF] text-sm hover:text-[#3395FF] transition-colors"
              data-testid="empty-state-clear-filter"
            >
              Mostra tutti i clienti
            </button>
          )}
        </div>
      )}

      <ClientDialog
        open={showClientDialog}
        onOpenChange={setShowClientDialog}
        editing={editingClient}
        form={clientForm}
        setForm={setClientForm}
        folders={folders}
        onSave={handleSaveClient}
      />

      <FolderDialog
        open={showFolderDialog}
        onOpenChange={setShowFolderDialog}
        form={folderForm}
        setForm={setFolderForm}
        onSave={handleSaveFolder}
      />

      <MoveClientDialog
        client={movingClient}
        folders={folders}
        targetFolderId={moveTargetFolder}
        setTargetFolderId={setMoveTargetFolder}
        onCancel={() => setMovingClient(null)}
        onConfirm={handleMoveClient}
      />
    </div>
  );
}
